import { useEffect, useMemo, useState } from "react";
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Form, FloatingLabel, Button } from "react-bootstrap";
import { RxDragHandleDots2 } from "react-icons/rx";
import "../common.css";

function rankingsFromBooks(books) {
  return books.reduce((acc, book, index) => {
    acc[(index + 1).toString()] = book.title;
    return acc;
  }, {});
}

function booksFromRankings(bookList, rankings) {
  if (!rankings || typeof rankings !== "object" || Array.isArray(rankings)) {
    return bookList;
  }

  if (!validateRankings(bookList, rankings)) {
    return bookList;
  }

  const booksByTitle = new Map(bookList.map((book) => [book.title, book]));
  return Object.keys(rankings)
    .sort((a, b) => Number(a) - Number(b))
    .map((rank) => booksByTitle.get(rankings[rank]));
}

const SortableBook = ({ book, rank, disabled }) => {
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: book.title, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`ranker-row${isDragging ? " ranker-row-dragging" : ""}`}
    >
      <div className="ranker-position">{rank}</div>
      <div className="ranker-book-title">{book.title}</div>
      <button
        ref={setActivatorNodeRef}
        type="button"
        className={`ranker-drag-handle${
          isDragging ? " ranker-drag-handle-dragging" : ""
        }`}
        aria-label={`Move ${book.title}`}
        disabled={disabled}
        {...attributes}
        {...listeners}
      >
        <RxDragHandleDots2 aria-hidden="true" />
      </button>
    </div>
  );
};

function isRankingValid(bookList, rankings, user) {
  return validateRankings(bookList, rankings) && user.trim() !== "";
}

function validateRankings(bookList, rankings) {
  const bookTitles = new Set(bookList.map((book) => book.title));
  const rankingValues = new Set(Object.keys(rankings));
  const rankingTitles = Array.from(new Set(Object.values(rankings)));

  if (
    rankingTitles.length !== bookTitles.size ||
    !rankingTitles.every((title) => bookTitles.has(title))
  ) {
    return false;
  }

  return Array.from({ length: bookList.length }, (_, i) =>
    (i + 1).toString()
  ).every((num) => rankingValues.has(num));
}

function Ranker({
  bookList,
  rankings,
  setRankings,
  user,
  setUser,
  submitRankings,
  alreadyRanked,
  savedRankings,
  savedUser,
}) {
  const initialBooks =
    alreadyRanked && savedRankings
      ? booksFromRankings(bookList, savedRankings)
      : bookList;
  const [orderedBooks, setOrderedBooks] = useState(initialBooks);
  const orderedBookIds = useMemo(
    () => orderedBooks.map((book) => book.title),
    [orderedBooks],
  );
  const sensors = useSensors(
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 5,
      },
    }),
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 6,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    if (alreadyRanked && savedRankings) {
      setOrderedBooks(booksFromRankings(bookList, savedRankings));
      return;
    }

    setOrderedBooks(bookList);
  }, [alreadyRanked, bookList, savedRankings]);

  useEffect(() => {
    setRankings(rankingsFromBooks(orderedBooks));
  }, [orderedBooks, setRankings]);

  function handleDragEnd(event) {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    setOrderedBooks((books) => {
      const oldIndex = books.findIndex((book) => book.title === active.id);
      const newIndex = books.findIndex((book) => book.title === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return books;
      }

      return arrayMove(books, oldIndex, newIndex);
    });
  }

  const displayedUser = alreadyRanked && savedUser ? savedUser : user;

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={orderedBookIds}
          strategy={verticalListSortingStrategy}
        >
          <div className="ranker-list">
            {orderedBooks.map((book, index) => (
              <SortableBook
                key={book.title}
                book={book}
                rank={index + 1}
                disabled={alreadyRanked}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <FloatingLabel controlId="rankerName" label="Your name" className="mb-4">
        <Form.Control
          type="text"
          placeholder="Your name"
          className="input-box"
          value={displayedUser}
          disabled={alreadyRanked}
          onChange={(e) => {
            setUser(e.target.value);
          }}
        />
      </FloatingLabel>
      <div className="centered-container">
        <Button
          className="button-tertiary"
          disabled={alreadyRanked || !isRankingValid(bookList, rankings, user)}
          onClick={submitRankings}
        >
          {alreadyRanked ? "Ranking Already Submitted" : "Submit Ranking"}
        </Button>
      </div>
    </>
  );
}

export default Ranker;
